import React, { useEffect, useState, useRef } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Stepper,
  Step,
  StepLabel,
  TextField,
  MenuItem,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Radio,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { IconButton, Tooltip } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useNotify, useRedirect } from 'react-admin';
import { httpClient, publicBase, adminBase } from './dataProvider';

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
  return new Date(dateString).toLocaleDateString(undefined, options);
};


const steps = ['1. Course', '2. Template', '3. Knowledge Base RAG', '4. Review & Generate'];

const CourseMaterialWizard = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [courses, setCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [templates, setTemplates] = useState([]);
  const [tplLoading, setTplLoading] = useState(false);
  const [tplError, setTplError] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  // RAG step state
  const [ragKbSource, setRagKbSource] = useState('');
  const [kbFiles, setKbFiles] = useState([]);
  const [kbFilesLoading, setKbFilesLoading] = useState(false);
  const [kbFilesError, setKbFilesError] = useState(null);
  const [saveAs, setSaveAs] = useState('draft');

  // File upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);


  // Generation state
  const [generating, setGenerating] = useState(false);
  const [streamStatus, setStreamStatus] = useState('idle'); // idle | connecting | generating | finalizing | done | error
  const [streamedText, setStreamedText] = useState('');
  const [chars, setChars] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef(null);
  const abortRef = useRef(null);

  // For future steps (to be implemented incrementally)

  useEffect(() => {
    setLoading(true);
    const url = `${publicBase}/courses/?_start=0&_end=50&_sort=created_at&_order=DESC&include_template=true&include_complexity=true`;
    httpClient(url)
      .then(({ json }) => {
        setCourses(Array.isArray(json) ? json : []);
      })
      .catch((e) => {
        console.error('Failed to load courses', e);
        const msg = e?.body?.detail || e?.message || 'Failed to load courses';
        setError(msg);
      })
      .finally(() => setLoading(false));
  }, []);

  // Next steps will derive selected course object when needed

  // Gate next button
  const canProceedFromStep = (step) => {
    if (step === 0) return !!selectedCourseId;
    if (step === 1) return !!selectedTemplateId;
    if (step === 2) return typeof ragKbSource === 'string' && ragKbSource.trim().length > 0;
    return true;
  };

  const handleNext = () => {
    if (!canProceedFromStep(activeStep)) return;
    setActiveStep((s) => Math.min(s + 1, steps.length - 1));
  };

  const handleBack = () => setActiveStep((s) => Math.max(s - 1, 0));

  // Load templates when entering Step 1, and preselect from selected course
  useEffect(() => {
    if (activeStep !== 1) return;
    // If already loaded, only try to preselect
    const currentCourse = courses.find((c) => c.id === selectedCourseId);
    if (templates.length > 0) {
      if (!selectedTemplateId && currentCourse?.prompt_template?.id) {
        setSelectedTemplateId(currentCourse.prompt_template.id);
      }
      return;
    }
    setTplLoading(true);
    setTplError(null);
    httpClient(`${adminBase}/prompt_templates/?_start=0&_end=100&_sort=created_at&_order=DESC`)
      .then(({ json }) => {
        const list = Array.isArray(json) ? json : [];
        setTemplates(list);
        // Preselect course template if present
        if (!selectedTemplateId && currentCourse?.prompt_template?.id) {
          setSelectedTemplateId(currentCourse.prompt_template.id);
        }
      })
      .catch((e) => {
        console.error('Failed to load templates', e);
        const msg = e?.body?.detail || e?.message || 'Failed to load templates';
        setTplError(msg);
      })
      .finally(() => setTplLoading(false));
  }, [activeStep, courses, selectedCourseId, selectedTemplateId, templates.length]);


  // Render course list with template summary
  const renderCourseSelection = () => (
    <>
      <Alert severity="info">
        Build new/extra course material. Select a course.
      </Alert>
      <List sx={{ width: '100%', bgcolor: 'background.paper', maxHeight: 420, overflow: 'auto' }}>
        {courses.map((course) => (
          <ListItem
            key={course.id}
            disablePadding
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
              mb: 1.5,
            }}
            secondaryAction={
              <Radio
                edge="end"
                checked={selectedCourseId === course.id}
                onChange={() => setSelectedCourseId(course.id)}
              />
            }
          >
            <ListItemButton onClick={() => setSelectedCourseId(course.id)} selected={selectedCourseId === course.id}>
              <ListItemText
                primary={course.title}
                secondary={
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <span style={{ flex: 1 }}>{course.description || 'No description'}</span>
                      <Tooltip title="Edit course">
                        <IconButton
                          component={RouterLink}
                          to={`/courses/${course.id}`}
                          size="small"
                          aria-label={`Edit ${course.title}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                    <Typography variant="body2" color="text.secondary" component="div">
                      <strong>Category:</strong> {course.category_name || 'N/A'}<br />
                      <strong>Complexity:</strong> {course.complexity_name || 'N/A'}<br />
                      <strong>Created At:</strong> {formatDate(course.created_at)}<br />
                      {course.updated_at && <>
                        <strong>Updated At:</strong> {formatDate(course.updated_at)}
                      </>}
                    </Typography>
                  </>
                }
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </>
  );

  const renderTemplateSelection = () => (
    <>
      {tplLoading && <CircularProgress />}
      {tplError && <Alert severity="error">{tplError}</Alert>}
      <TextField
        select
        label="Prompt Template"
        value={selectedTemplateId}
        onChange={(e) => setSelectedTemplateId(e.target.value)}
        helperText="Choose or confirm a prompt template"
        fullWidth
      >
        {templates.map((t) => (
          <MenuItem key={t.id} value={t.id}>
            {t.name}{t.description ? ` — ${t.description}` : ''}
          </MenuItem>
        ))}
      </TextField>
    </>
  );

  // Load KB files when entering Step 2
  useEffect(() => {
    if (activeStep !== 2) return;
    if (kbFiles.length > 0) return; // Already loaded

    setKbFilesLoading(true);
    setKbFilesError(null);
    httpClient(`${publicBase}/kb_files/`)
      .then(({ json }) => {
        const files = Array.isArray(json) ? json : [];
        setKbFiles(files);
        // Auto-select first file if none selected
        if (!ragKbSource && files.length > 0) {
          setRagKbSource(files[0].path);
        }
      })
      .catch((e) => {
        console.error('Failed to load KB files', e);
        const msg = e?.body?.detail || e?.message || 'Failed to load KB files';
        setKbFilesError(msg);
      })
      .finally(() => setKbFilesLoading(false));
  }, [activeStep, kbFiles.length, ragKbSource]);

  // Handle file upload
  const handleFileUpload = async () => {
    if (!selectedFile) {
      notify('Please select a file first', { type: 'warning' });
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await httpClient(`${publicBase}/kb_files/upload`, {
        method: 'POST',
        body: formData,
      });

      // Update the ragKbSource with the uploaded filename
      setRagKbSource(response.json.filename);

      // Refresh the KB files list
      const { json } = await httpClient(`${publicBase}/kb_files/`);
      setKbFiles(Array.isArray(json) ? json : []);

      notify('File uploaded successfully!', { type: 'success' });
      setSelectedFile(null); // Clear the selected file
    } catch (error) {
      console.error('File upload failed:', error);
      const errorMsg = error?.body?.detail || error?.message || 'Failed to upload file';
      setUploadError(errorMsg);
      notify(errorMsg, { type: 'error' });
    } finally {
      setUploading(false);
    }
  };

  // Step 2: RAG KB Source selector with dropdown

  const renderRagKbSource = () => (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>
        Select a Knowledge Base file to use for RAG (Retrieval-Augmented Generation).
        The selected file will provide context for generating course content.
      </Alert>

      {/* File Upload Section */}
      <Card variant="outlined" sx={{ mb: 3, p: 2, bgcolor: '#f9f9f9' }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
          Upload Knowledge Base File
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
          <Button
            variant="outlined"
            component="label"
            disabled={uploading}
          >
            Choose File
            <input
              type="file"
              hidden
              accept=".md,.txt"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setSelectedFile(file);
                  setUploadError(null);
                }
              }}
            />
          </Button>
          <Typography variant="body2" sx={{ flex: 1 }}>
            {selectedFile ? `Selected: ${selectedFile.name} (${(selectedFile.size / 1024).toFixed(1)} KB)` : 'No file selected'}
          </Typography>
          <Button
            variant="contained"
            onClick={handleFileUpload}
            disabled={!selectedFile || uploading}
            startIcon={uploading ? <CircularProgress size={20} /> : null}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </Stack>
        {uploadError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {uploadError}
          </Alert>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Accepted formats: .md, .txt (max 10MB)
        </Typography>
      </Card>

      {/* Divider */}
      <Typography variant="body2" color="text.secondary" align="center" sx={{ my: 2 }}>
        — OR —
      </Typography>

      {/* Existing KB Files Dropdown */}
      {kbFilesLoading && <CircularProgress size={24} />}
      {kbFilesError && <Alert severity="error" sx={{ mb: 2 }}>{kbFilesError}</Alert>}
      <FormControl fullWidth>
        <InputLabel id="kb-file-label">Knowledge Base File</InputLabel>
        <Select
          labelId="kb-file-label"
          value={ragKbSource}
          label="Knowledge Base File"
          onChange={(e) => setRagKbSource(e.target.value)}
        >
          {kbFiles.map((file) => (
            <MenuItem key={file.path} value={file.path}>
              {file.filename} ({file.size_kb} KB)
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Manual Path Input */}
      <TextField
        label="Or enter custom path"
        value={ragKbSource}
        onChange={(e) => setRagKbSource(e.target.value)}
        helperText="You can also type a KB filename directly (e.g., kb_management_gui.md)"
        fullWidth
        sx={{ mt: 2 }}
        size="small"
      />
    </>
  );


  // Step 3: Review & Generate
  const currentCourse = courses.find((c) => c.id === selectedCourseId);
  const currentTemplate = templates.find((t) => t.id === selectedTemplateId);

  const renderReview = () => (
    <>
      <Typography variant="subtitle1">Review</Typography>
      <div><strong>Course Title:</strong> {currentCourse ? currentCourse.title : '-'}</div>
      <div><strong>Category:</strong> {currentCourse?.category_name ?? '-'}</div>
      <div><strong>Complexity:</strong> {currentCourse?.complexity_name ?? '-'}</div>
      <div style={{ whiteSpace: 'pre-wrap' }}><strong>Description:</strong> {currentCourse?.description ?? '-'}</div>
      <div><strong>Template:</strong> {currentTemplate ? currentTemplate.name : '-'}</div>
      <div><strong>RAG KB Source:</strong> {ragKbSource || '-'}</div>
      <TextField
        select
        label="Save As"
        value={saveAs}
        onChange={(e) => setSaveAs(e.target.value)}
        fullWidth
        sx={{ mt: 2 }}
      >
        <MenuItem value="draft">Draft</MenuItem>
        <MenuItem value="final">Final</MenuItem>
      </TextField>
    </>
  );

  const handleGenerate = async () => {
    console.log('🚀 Generate button clicked - starting course material generation');

    // Track if generation completed successfully (even if post-completion actions fail)
    let generationSucceeded = false;
    let materialId = null;
    let lastProgressUpdate = '';

    try {
      // Validation checks
      if (!selectedCourseId) {
        console.log('❌ Validation failed: No course selected');
        notify('Please select a course', { type: 'warning' });
        return;
      }
      if (!selectedTemplateId) {
        console.log('❌ Validation failed: No template selected');
        notify('Please select a template', { type: 'warning' });
        return;
      }
      if (!ragKbSource || !ragKbSource.trim()) {
        console.log('❌ Validation failed: No RAG KB source provided');
        notify('Please provide a RAG KB Source', { type: 'warning' });
        return;
      }

      // Set loading state and prepare streaming UI
      console.log('⏳ Setting generating state to true - button should be disabled');
      setGenerating(true);
      setStreamStatus('connecting');
      setStreamedText('');
      setChars(0);
      setElapsed(0);
      if (timerRef.current) window.clearInterval(timerRef.current);
      const t0 = Date.now();
      timerRef.current = window.setInterval(() => setElapsed(Math.floor((Date.now() - t0) / 1000)), 1000);

      // Build course_meta from selected course so the backend has context to customize output
      const courseMeta = {
        rag_kb_source: ragKbSource.trim(),
      };
      if (currentCourse) {
        courseMeta.title = currentCourse.title || '';
        courseMeta.category = currentCourse.category_name || '';
        courseMeta.complexity = currentCourse.complexity_name || '';
        courseMeta.description = currentCourse.description || '';
      }

      const payload = {
        prompt_template_id: selectedTemplateId,
        params: {},
        course_meta: courseMeta,
        save_as: saveAs,
        // course_id is required: we collected it in step 1
        course_id: selectedCourseId,
      };

      console.log('📤 Sending request to OpenAI generation endpoint with payload:', payload);

      // Stream from backend SSE endpoint (POST with text/event-stream response)
      const url = `${publicBase}/courses/generate_in_chunks`;
      const userId = localStorage.getItem('userId');
      if (!userId) throw new Error('User not authenticated - no user ID available');
      const token = localStorage.getItem('token');

      const controller = new AbortController();
      abortRef.current = controller;

      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          'X-User-Id': userId,
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      if (!res.ok || !res.body) {
        const txt = await res.text().catch(() => '');
        throw new Error(`Streaming request failed: ${res.status} ${txt}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      setStreamStatus('generating');

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        // Parse SSE lines separated by \n\n
        let idx;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
          const packet = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);

          const lines = packet.split('\n');
          let event = 'message';
          let data = '';
          for (const line of lines) {
            if (line.startsWith('event:')) {
              event = line.slice(6).trim();
            } else if (line.startsWith('data:')) {
              data += line.slice(5).trim();
            }
          }

          try {
            const payload = data ? JSON.parse(data) : {};
            if (event === 'response.created') {
              setStreamStatus('generating');
            } else if (event === 'response.output_text.delta') {
              const delta = payload?.delta ?? '';
              if (delta) {
                setStreamedText(prev => {
                  const next = prev + delta;
                  setChars(next.length);
                  // Track progress - look for chapter completion patterns
                  const chapterMatches = next.match(/chapter \d+\/\d+/gi);
                  if (chapterMatches && chapterMatches.length > 0) {
                    lastProgressUpdate = chapterMatches[chapterMatches.length - 1];
                  }
                  return next;
                });
              }
            } else if (event === 'heartbeat') {
              // no-op, keeps connection alive
            } else if (event === 'response.completed') {
              setStreamStatus('done');
              // Mark generation as successful - even if subsequent actions fail
              generationSucceeded = true;
              materialId = payload?.material_id;
            } else if (event === 'error') {
              const msg = payload?.message || 'Streaming error';
              throw new Error(msg);
            }
          } catch (err) {
            // Re-throw actual errors (not just parsing issues)
            if (err.message && !err.message.includes('JSON')) {
              throw err;
            }
            // If parsing fails, log and continue
            console.warn('SSE parse warn:', err);
          }
        }
      }

      // Show success message and redirect
      if (generationSucceeded) {
        notify(`Course material generated successfully!${materialId ? ` (ID: ${materialId.slice(0, 8)}...)` : ''}`, { type: 'success' });
        // Redirect to course wizard list after a short delay
        setTimeout(() => redirect('/course_wizard'), 1500);
      } else {
        // Stream ended without completion event - this is unexpected
        const progressInfo = lastProgressUpdate ? ` (Last progress: ${lastProgressUpdate})` : '';
        notify(`Generation stream ended unexpectedly.${progressInfo} Please try again.`, { type: 'warning' });
      }

    } catch (e) {
      console.error('❌ Generate error - full error object:', e);
      console.error('❌ Error body:', e?.body);
      console.error('❌ Error message:', e?.message);
      console.error('❌ Error stack:', e?.stack);

      // If generation already succeeded, don't show error for post-completion failures
      if (generationSucceeded) {
        console.log('✅ Generation succeeded, ignoring post-completion error');
        notify(`Course material generated successfully!${materialId ? ` (ID: ${materialId.slice(0, 8)}...)` : ''}`, { type: 'success' });
        setTimeout(() => redirect('/course_wizard'), 1500);
        return;
      }

      // Build meaningful error message
      let msg = 'Generation failed';
      const progressInfo = lastProgressUpdate ? ` (Progress: ${lastProgressUpdate})` : '';

      // Detect common error types and provide helpful messages
      const errorMessage = e?.message || e?.body?.detail || '';

      if (errorMessage.includes('BodyStreamBuffer') || errorMessage.includes('aborted')) {
        msg = `Connection was interrupted${progressInfo}. This may be a network issue. Please try again.`;
      } else if (errorMessage.includes('network error') || errorMessage.includes('ERR_')) {
        msg = `Network error occurred${progressInfo}. Please check your connection and try again.`;
      } else if (errorMessage.includes('JSON')) {
        msg = `OpenAI returned an invalid response${progressInfo}. Please try again.`;
      } else if (e?.body?.detail) {
        msg = e.body.detail + progressInfo;
      } else if (e?.message) {
        msg = e.message + progressInfo;
      } else {
        msg = `An unexpected error occurred${progressInfo}. Please try again.`;
      }

      setStreamStatus('error');
      notify(msg, { type: 'error' });
    } finally {
      console.log('🔄 Resetting generating state to false - button should be re-enabled');
      setGenerating(false);
      if (timerRef.current) { window.clearInterval(timerRef.current); timerRef.current = null; }
      abortRef.current = null;
    }
  };

  const handleCancel = () => {
    try {
      if (abortRef.current) abortRef.current.abort();
    } catch { }
    setStreamStatus('idle');
    setGenerating(false);
    if (timerRef.current) { window.clearInterval(timerRef.current); timerRef.current = null; }
  };

  if (loading) return <CircularProgress />;

  return (
    <Card sx={{ m: 2 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Course Material Wizard
        </Typography>
        <Stepper activeStep={activeStep} sx={{ mb: 2 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          {activeStep === 0 && renderCourseSelection()}
          {activeStep === 1 && renderTemplateSelection()}
          {activeStep === 2 && renderRagKbSource()}
          {activeStep === 3 && renderReview()}
          {generating && (
            <Alert severity="info">
              <strong>Status:</strong> {streamStatus} • <strong>Elapsed:</strong> {elapsed}s • <strong>Chars:</strong> {chars}
            </Alert>
          )}
          {streamedText && (
            <pre style={{ maxHeight: 260, overflow: 'auto', background: '#f7f7f7', padding: 12, borderRadius: 6 }}>
              {streamedText}
            </pre>
          )}
        </Stack>
      </CardContent>
      <CardActions>
        <Button onClick={handleBack} disabled={activeStep === 0}>Back</Button>
        {activeStep < steps.length - 1 ? (
          <Button variant="contained" onClick={handleNext} disabled={!canProceedFromStep(activeStep)}>
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            onClick={handleGenerate}
            disabled={generating}
            startIcon={generating ? <CircularProgress size={20} /> : null}
          >
            {generating ? 'Generating...' : 'Generate'}
          </Button>
        )}
        {generating && (
          <Button onClick={handleCancel} color="secondary">Cancel</Button>
        )}
      </CardActions>
    </Card>
  );
};

export default CourseMaterialWizard;
